import { Card, Tooltip } from '@mantine/core';
import { ExternalLink as ExternalLinkIcon, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { buildFeatureUnavailableBody, buildFeatureUnavailableHeadline } from '@/utils/feature-availability-utils.ts';
import { cn } from '@/utils/style-utils.ts';

// A tile points at exactly one destination: an internal route or an external URL.
export type StartTileDestination = { to: string } | { href: string };

interface Props {
    icon: LucideIcon;
    title: string;
    description: string;
    destination: StartTileDestination;
    disabled?: boolean;
}

const cardBaseClassName = 'h-full border-[2px] border-border';
const cardInteractiveClassName =
    'group cursor-pointer transition-all hover:-translate-y-1 hover:border-primary hover:shadow-md';
const cardDisabledClassName = 'opacity-50';

export const StartTile = ({ icon: Icon, title, description, destination, disabled = false }: Props) => {
    const isExternal = 'href' in destination;
    const body = (
        <Card.Section className="flex h-full flex-col gap-3 px-6 py-7">
            <div className="flex items-center justify-between">
                <Icon className="size-8 text-primary" />
                {isExternal && <ExternalLinkIcon className="size-4 text-muted-foreground" />}
            </div>
            <div className="text-xl tracking-tight text-foreground">{title}</div>
            <p className="text-sm text-muted-foreground">{description}</p>
        </Card.Section>
    );

    if (disabled) {
        return (
            <Tooltip
                multiline
                position="bottom"
                label={
                    <div className="max-w-[300px] space-y-1">
                        <div className="font-semibold">{buildFeatureUnavailableHeadline(title)}</div>
                        <div>{buildFeatureUnavailableBody(title)}</div>
                    </div>
                }
            >
                <div aria-label={title} aria-disabled className="block">
                    <Card className={cn(cardBaseClassName, cardDisabledClassName)}>{body}</Card>
                </div>
            </Tooltip>
        );
    }

    if (isExternal) {
        return (
            <a
                href={destination.href}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={title}
                className="block no-underline"
            >
                <Card className={cn(cardBaseClassName, cardInteractiveClassName)}>{body}</Card>
            </a>
        );
    }

    return (
        <Link to={destination.to} aria-label={title} className="block no-underline">
            <Card className={cn(cardBaseClassName, cardInteractiveClassName)}>{body}</Card>
        </Link>
    );
};
